import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as SliderPrimitive from '@radix-ui/react-slider';
import { cn } from '@discover-lib/utils';
import { DEFAULT_STEP_MINUTES } from '@discover-data/stores/restaurant/model/times/default';
import TimeValue from './value';

type TimeRangePair = { startMinute: number; endMinute: number };

type DoubleRangeSliderProps = {
  className?: string;
  minMax: TimeRangePair;
  defaultValue?: Partial<TimeRangePair>;
  onValueChange?: (values: { startMinute: number | null; endMinute: number | null }) => void;
  step?: number;
};

const DoubleRangeSlider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  DoubleRangeSliderProps
>(
  (
    { className, minMax, defaultValue = {}, step = DEFAULT_STEP_MINUTES, onValueChange, ...props },
    ref
  ) => {
    const { startMinute = minMax.startMinute, endMinute = minMax.endMinute } = defaultValue;

    const [localValues, setLocalValues] = useState<TimeRangePair>({
      startMinute,
      endMinute,
    });

    // Reset local state when default values or range changes
    useEffect(() => {
      setLocalValues({ startMinute, endMinute });
    }, [startMinute, endMinute]);

    const [showCombinedTooltip, setShowCombinedTooltip] = useState(false);

    const sliderRef = useRef<HTMLDivElement>(null);

    const totalRange = minMax.endMinute - minMax.startMinute;

    useEffect(() => {
      const handleDistance = localValues.endMinute - localValues.startMinute;
      const shouldShow = (handleDistance / totalRange) * 100 < 20 || handleDistance <= 120;

      setShowCombinedTooltip((prev) => (prev !== shouldShow ? shouldShow : prev));
    }, [localValues, totalRange]);

    const handleValueUpdate = useCallback(
      (newValues: number[], commit = false) => {
        const [newStart, newEnd] = newValues;
        setLocalValues({ startMinute: newStart, endMinute: newEnd });

        if (commit && onValueChange) {
          onValueChange({
            startMinute: newStart === minMax.startMinute ? null : newStart,
            endMinute: newEnd === minMax.endMinute ? null : newEnd,
          });
        }
      },
      [minMax, onValueChange]
    );

    const handleValueChange = useCallback(
      (newValues: number[]) => handleValueUpdate(newValues),
      [handleValueUpdate]
    );

    const handleValueCommit = useCallback(
      (newValues: number[]) => handleValueUpdate(newValues, true),
      [handleValueUpdate]
    );

    const getCombinedTooltipPosition = (rangeValues: TimeRangePair) => {
      const midpoint = (rangeValues.startMinute + rangeValues.endMinute) / 2;
      const percentage =
        ((midpoint - minMax.startMinute) / (minMax.endMinute - minMax.startMinute)) * 100;
      // Clamp the position between 0% and 100%
      return `${Math.max(0, Math.min(100, percentage))}%`;
    };

    return (
      <div ref={sliderRef} className="relative px-5 md:px-2">
        <SliderPrimitive.Root
          ref={ref}
          className={cn(
            'relative flex w-full cursor-grab touch-none select-none items-center',
            className
          )}
          min={minMax.startMinute}
          max={minMax.endMinute}
          step={step}
          value={[localValues.startMinute, localValues.endMinute]}
          onValueChange={handleValueChange}
          onValueCommit={handleValueCommit}
          minStepsBetweenThumbs={1}
          {...props}>
          <SliderPrimitive.Track className="relative h-2 w-full grow overflow-hidden rounded-full bg-gray-200">
            <SliderPrimitive.Range className="absolute h-full cursor-default bg-primary" />
          </SliderPrimitive.Track>
          <div
            className={cn('absolute top-6 z-20 w-max', { hidden: !showCombinedTooltip })}
            style={{
              left: getCombinedTooltipPosition(localValues),
              transform: 'translateX(-50%)',
            }}>
            <div className="items-center whitespace-nowrap rounded bg-white px-2 py-1 text-xs shadow">
              {localValues.startMinute === localValues.endMinute ? (
                <TimeValue minutes={localValues.startMinute} />
              ) : (
                <>
                  <TimeValue minutes={localValues.startMinute} /> -{' '}
                  <TimeValue minutes={localValues.endMinute} />
                </>
              )}
            </div>
          </div>
          {[localValues.startMinute, localValues.endMinute].map((value, index) => (
            <SliderPrimitive.Thumb
              key={index}
              className="block size-5 rounded-full border-2 border-primary bg-background ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50">
              <div
                className={cn('absolute top-6 w-max', { hidden: showCombinedTooltip })}
                style={{
                  left: '50%',
                  transform: 'translateX(-50%)',
                }}>
                <div className="whitespace-nowrap rounded bg-white px-2 py-1 text-xs shadow">
                  <TimeValue minutes={value} />
                </div>
              </div>
            </SliderPrimitive.Thumb>
          ))}
        </SliderPrimitive.Root>
      </div>
    );
  }
);

DoubleRangeSlider.displayName = 'DoubleRangeSlider';

export default DoubleRangeSlider;
